import React, { useState, useEffect } from 'react';
import { reviewAPI } from '../../api/reviews';
import { format } from 'date-fns';
import { StarIcon } from '@heroicons/react/24/solid';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import toast from 'react-hot-toast';

const MyReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({
    rating: 0,
    comment: '',
    feedback: ''
  });

  useEffect(() => {
    loadMyReviews();
  }, []);

  const loadMyReviews = async () => {
    try {
      setLoading(true);
      const response = await reviewAPI.getMyReviews();
      setReviews(response.data.data || []);
    } catch (error) {
      console.error('Failed to load reviews:', error);
      toast.error('Failed to load your reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (review) => {
    setEditingReview(review.id);
    setEditForm({
      rating: review.rating,
      comment: review.comment || '',
      feedback: review.feedback || ''
    });
  };

  const handleUpdate = async () => {
    try {
      await reviewAPI.update(editingReview, editForm);
      toast.success('Review updated successfully');
      setEditingReview(null);
      loadMyReviews();
    } catch (error) {
      console.error('Failed to update review:', error);
      toast.error('Failed to update review');
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      await reviewAPI.delete(reviewId);
      toast.success('Review deleted successfully');
      loadMyReviews();
    } catch (error) {
      console.error('Failed to delete review:', error);
      toast.error('Failed to delete review');
    }
  };

  const renderStars = (rating, interactive = false, onRatingChange = null) => {
    return Array.from({ length: 5 }, (_, index) => (
      <button
        key={index}
        type="button"
        className={`focus:outline-none ${interactive ? '' : 'pointer-events-none'}`}
        onClick={interactive ? () => onRatingChange(index + 1) : undefined}
      >
        <StarIcon
          className={`h-5 w-5 ${
            index < rating ? 'text-yellow-400' : 'text-gray-300'
          }`}
        />
      </button>
    ));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">My Reviews</h1>

        {reviews.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">You haven't written any reviews yet.</p>
            <p className="text-gray-400 mt-2">Complete a booking to leave your first review!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {reviews.map((review) => (
              <div key={review.id} className="border rounded-lg p-6">
                {editingReview === review.id ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Rating *</label>
                      <div className="flex space-x-1">
                        {renderStars(editForm.rating, true, (rating) =>
                          setEditForm({ ...editForm, rating })
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Comment</label>
                      <textarea
                        value={editForm.comment}
                        onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
                        className="w-full input min-h-[80px] resize-none"
                        maxLength={500}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Feedback</label>
                      <textarea
                        value={editForm.feedback}
                        onChange={(e) => setEditForm({ ...editForm, feedback: e.target.value })}
                        className="w-full input min-h-[60px] resize-none"
                        maxLength={300}
                      />
                    </div>

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setEditingReview(null)}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdate}
                        className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                      >
                        Update Review
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{review.business_name}</h3>
                        <div className="flex items-center mt-1">
                          {renderStars(review.rating)}
                          <span className="ml-2 text-sm text-gray-600">
                            {format(new Date(review.created_at), 'MMMM dd, yyyy')}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(review)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                          title="Edit review"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(review.id)}
                          className="p-2 text-gray-400 hover:text-red-600"
                          title="Delete review"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    {review.comment && (
                      <p className="text-gray-700 mb-3">{review.comment}</p>
                    )}

                    {review.feedback && (
                      <div className="bg-gray-50 rounded p-3">
                        <p className="text-sm text-gray-600">
                          <strong>Feedback:</strong> {review.feedback}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReviews;